import axios from 'axios';
import apiConfig from '../../config/apiConfig.js';

export interface MessageData {
  year: number;
  text: string;
  audioUrl: string;
}

export interface AddMessagesRequest {
  messages: MessageData[];
  messageType?: 'Digital' | 'Physical';
}

export interface MessageResponse {
  status: 'Success' | 'Failed';
  message: string;
  data?: any;
}

export interface AudioUploadResponse {
  status: 'Success' | 'Failed';
  message: string;
  audioUrl?: string;
}

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` })
  };
};

const getAuthHeadersForFormData = () => {
  const token = localStorage.getItem('authToken');
  return {
    ...(token && { Authorization: `Bearer ${token}` })
  };
};

export const messageApi = {
  // Test API connectivity
  testConnection: async (): Promise<boolean> => {
    try {
      console.log('🧪 Testing API connectivity...');
      const response = await axios.get(`${apiConfig.apiBaseURL}/health`, { 
        timeout: 5000,
        headers: getAuthHeaders()
      });
      console.log('✅ API connectivity test passed');
      return true;
    } catch (error) {
      console.log('❌ API connectivity test failed:', error);
      return false;
    }
  },

  // Step 2: Upload messages with audio URLs to gift
  addGiftMessages: async (giftId: string, messagesData: AddMessagesRequest): Promise<MessageResponse> => {
    try {
      console.log('🌐 Step 2: Adding gift messages with audio URLs:', { giftId, messagesData });
      
      const response = await axios.post(
        `${apiConfig.apiBaseURL}/gift/add-gift-messages/${giftId}`,
        messagesData,
        {
          headers: getAuthHeaders()
        }
      );
      
      console.log('Add messages response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error adding messages:', error);
      console.error('Error response:', error.response?.data);
      
      // Better error handling
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Failed to add messages';
      
      throw new Error(errorMessage);
    }
  },

  // Step 1: Upload audio file to get URL - NO gift ID needed
  uploadAudioFile: async (audioFile: File, abortSignal?: AbortSignal): Promise<AudioUploadResponse> => {
    console.log('Uploading audio file:', { 
      name: audioFile.name, 
      size: audioFile.size, 
      type: audioFile.type 
    });
    
    // Calculate dynamic timeout based on file size (moved to function scope)
    const fileSizeMB = audioFile.size / (1024 * 1024);
    const dynamicTimeout = Math.max(60000, 60000 + (fileSizeMB * 20000)); // Min 60s, +20s per MB
    
    const formData = new FormData();
    formData.append('audio', audioFile);
    
    // Debug: Log what we're sending
    console.log('🔍 FormData contents:');
    for (let [key, value] of formData.entries()) {
      console.log(`  ${key}:`, value);
    }

    // Step 1: Upload audio file to get URL - NO gift ID needed
    const uploadUrl = `${apiConfig.apiBaseURL}/upload-audio`;
    console.log('🌐 Step 1: Uploading audio to get URL:', uploadUrl);

    console.log(`📊 Upload timeout calculation:`, {
      fileSizeMB: fileSizeMB.toFixed(2),
      timeoutSeconds: (dynamicTimeout / 1000).toFixed(1),
      baseTimeout: '60s',
      additionalTime: `+${((dynamicTimeout - 60000) / 1000).toFixed(1)}s`
    });

    try {

      const response = await axios.post(
        uploadUrl,
        formData,
        {
          headers: getAuthHeadersForFormData(),
          timeout: dynamicTimeout, // Dynamic timeout based on file size
          signal: abortSignal, // Add abort signal support
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
            console.log(`Upload progress: ${percentCompleted}%`);
          }
        }
      );
  
      console.log(`✅ Upload successful:`, response.data);
      
      // Validate response structure
      if (!response.data) {
        throw new Error('Invalid response format from server');
      }

      // Check for success status regardless of case
      const responseData = response.data;
      const status = responseData.status;
      
      if (status === 'Success') {
        console.log(`✅ Upload completed successfully!`);
        return {
          status: 'Success',
          message: responseData.message || 'Audio uploaded successfully',
          audioUrl: responseData.url // Backend returns 'url' field
        };
      } else {
        return {
          status: 'Failed',
          message: responseData.message || 'Upload failed'
        };
      }
    } catch (error: any) {
      console.error('🚨 Error uploading audio:', error);
      console.error('🚨 Error response:', error.response?.data);
      console.error('🚨 Error status:', error.response?.status);
      console.error('🚨 Error config:', error.config?.url);
      
      // Check if this is an abort error (user cancelled) - throw it to be handled upstream
      const isAbortedError = error.name === 'AbortError' || 
                            error.name === 'CancelledError' ||
                            error.code === 'ERR_CANCELED' ||
                            error.message?.toLowerCase().includes('abort') ||
                            error.message?.toLowerCase().includes('cancel');
      
      if (isAbortedError) {
        console.log('🚫 Upload was aborted by user - throwing abort error');
        throw error; // Re-throw abort errors to be handled in the calling function
      }
      
      // Enhanced error handling with more specific network diagnostics
      let errorMessage = 'Failed to upload audio file';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.code === 'ECONNABORTED') {
        const fileSizeMB = (audioFile.size / (1024 * 1024)).toFixed(2);
        errorMessage = `Upload timeout after ${(dynamicTimeout / 1000).toFixed(1)}s for ${fileSizeMB}MB file. Please try again or use a smaller file.`;
      } else if (error.code === 'ERR_NETWORK') {
        errorMessage = 'Network error - please check your connection and API server';
      } else if (error.response?.status === 404) {
        errorMessage = 'Upload endpoint not found - API endpoint may be incorrect';
      } else if (error.response?.status === 413) {
        errorMessage = 'File too large - please use a smaller audio file';
      } else if (error.response?.status === 415) {
        errorMessage = 'Unsupported file format - please use a valid audio file';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error - please try again later';
      } else if (error.message === 'Network Error') {
        errorMessage = 'Cannot connect to server - please check if the API server is running';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        status: 'Failed',
        message: errorMessage
      };
    }
  },

  // Get single gift (includes messages) - FIXED: Corrected endpoint path
  getGiftWithMessages: async (giftId: string): Promise<MessageResponse> => {
    try {
      console.log('Fetching gift with messages:', giftId);
      
      const response = await axios.get(
        `${apiConfig.apiBaseURL}/gift/get-single-gift/${giftId}`, // Fixed: removed 's' from 'gifts'
        {
          headers: getAuthHeaders()
        }
      );
      
      console.log('Get gift response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching gift with messages:', error);
      console.error('Error response:', error.response?.data);
      
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Failed to fetch gift';
      
      throw new Error(errorMessage);
    }
  },

  // Update messages for a gift - FIXED: Corrected endpoint path
  updateGiftMessages: async (giftId: string, messagesData: AddMessagesRequest): Promise<MessageResponse> => {
    try {
      console.log('Updating gift messages:', { giftId, messagesData });
      
      const response = await axios.put(
        `${apiConfig.apiBaseURL}/gift/update-gift-messages/${giftId}`, // Fixed: removed 's' from 'gifts'
        messagesData,
        {
          headers: getAuthHeaders()
        }
      );
      
      console.log('Update messages response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error updating messages:', error);
      console.error('Error response:', error.response?.data);
      
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Failed to update messages';
      
      throw new Error(errorMessage);
    }
  },

  // Get all gifts (for listing) - FIXED: Corrected endpoint path
  getAllGifts: async (params?: { search?: string, status?: string, page?: number, limit?: number }): Promise<MessageResponse> => {
    try {
      console.log('Fetching all gifts with params:', params);
      
      const response = await axios.get(
        `${apiConfig.apiBaseURL}/gift/get-all-gifts`, // Fixed: removed 's' from 'gifts'
        {
          headers: getAuthHeaders(),
          params
        }
      );
      
      console.log('Get all gifts response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching gifts:', error);
      console.error('Error response:', error.response?.data);
      
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Failed to fetch gifts';
      
      throw new Error(errorMessage);
    }
  }
};